<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Datapack } from 'mc-datapack-loader';
import MainMap from './components/MainMap.vue';
import SettingsPanel from './components/SettingsPanel.vue';
import DatapackList from './components/DatapackList.vue';
import Footer from './components/Footer.vue';
import MenuButtons from './components/MenuButtons.vue';
import StructureDropdown from './components/dropdown/StructureDropdown.vue';
import FindBiomeDropdown from './components/dropdown/FindBiomeDropdown.vue';
import { useLoadedDimensionStore } from './stores/useLoadedDimensionStore';
import { useDatapackStore } from './stores/useDatapackStore';
import { useRecentStore } from './stores/useRecentStore';
import { useSearchStore } from './stores/useBiomeSearchStore';
import { useSettingsStore } from './stores/useSettingsStore';
import { EventTracker } from './util/EventTracker';
import { updateUrlParam, versionMetadata } from './util';

type PanelName = 'structures' | 'biomes' | 'datapacks' | 'about';

const i18n = useI18n()
const loaded = ref(false)
const activePanel = ref<PanelName>('structures')
const fileDragging = ref(false)
const mapFrame = ref<HTMLElement | null>(null)

const MIN_MAP_HEIGHT = 220
const MAX_MAP_VIEWPORT_RATIO = 0.82

const datapackStore = useDatapackStore()
const recentStore = useRecentStore()
const searchStore = useSearchStore()
const settingsStore = useSettingsStore()

const activeHeader = computed(() => {
    if (activePanel.value === 'datapacks') return 'datapacks'
    if (activePanel.value === 'about') return 'about'
    return 'map'
})

onBeforeMount(async () => {
    const loadedDimensionStore = useLoadedDimensionStore()
    for (const file of await recentStore.getLastDatapackFiles()) {
        try {
            const datapack = Datapack.fromZipFile(file, versionMetadata[settingsStore.mc_version].datapackFormat)
            datapackStore.addDatapack(datapack, file.name)
        } catch {
            // Ignore packs that cannot be read anymore.
        }
    }
    await recentStore.setLastDatapackNames(loadedDatapackNames())

    await loadedDimensionStore.reload()
    loaded.value = true
})

// Keep the stored pack list in sync with the packs currently loaded, so removed
// packs stay removed and every added pack comes back after a reload.
watch(loadedDatapackNames, (names) => {
    void recentStore.setLastDatapackNames(names)
})

function loadedDatapackNames(): string[] {
    return datapackStore.datapacks.flatMap(datapack => datapack.name === undefined ? [] : [datapack.name])
}

function showPanel(panel: PanelName) {
    activePanel.value = panel
    document.querySelector('.control-panel')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}

function changeLocale(event: Event) {
    const locale = (event.target as HTMLSelectElement).value
    i18n.locale.value = locale
    updateUrlParam('lang', locale)
}

async function dropHandler(ev: DragEvent) {
    if (ev.dataTransfer === null) {
        return
    }

    ev.preventDefault()
    fileDragging.value = false

    const datapackVersion = versionMetadata[settingsStore.mc_version].datapackFormat

    // The data transfer is only readable while the handler runs synchronously,
    // so all entries are captured before the first await to support dropping
    // several packs at once.
    const dropped = [...ev.dataTransfer.items].map(item => {
        const dataTransferItem = item as DataTransferItem
        return {
            handle: "getAsFileSystemHandle" in dataTransferItem ? dataTransferItem.getAsFileSystemHandle() : undefined,
            file: dataTransferItem.getAsFile()
        }
    })

    for (const entry of dropped) {
        try {
            // File system handles are only available for real drops, otherwise
            // fall back to the plain file.
            const handle = entry.handle === undefined ? undefined : await entry.handle.catch(() => undefined)

            if (handle instanceof FileSystemDirectoryHandle) {
                EventTracker.track(`add_datapack/folder/drag_and_drop`)
                const datapack = Datapack.fromFileSystemDirectoryHandle(handle, datapackVersion)
                datapackStore.addDatapack(datapack, handle.name)
                recentStore.addRecentFileHandle(handle, datapack)
            } else if (handle instanceof FileSystemFileHandle) {
                EventTracker.track(`add_datapack/zip/drag_and_drop`)
                const file = await handle.getFile()
                const datapack = Datapack.fromZipFile(file, datapackVersion)
                datapackStore.addDatapack(datapack, file.name)
                recentStore.storeAndAddRecent(file, datapack)
            } else if (entry.file !== null && ["application/zip", 'application/java-archive', 'application/x-java-archive'].includes(entry.file.type)) {
                EventTracker.track(`add_datapack/zip/drag_and_drop`)
                const datapack = Datapack.fromZipFile(entry.file, datapackVersion)
                datapackStore.addDatapack(datapack, entry.file.name)
                recentStore.storeAndAddRecent(entry.file, datapack)
            }
        } catch (e) {
            console.warn(`Failed to load dropped datapack: ${e}`)
        }
    }
}

function dragOverHandler(ev: DragEvent) {
    ev.preventDefault()
}

function clampMapHeight(height: number) {
    return Math.min(
        Math.max(height, MIN_MAP_HEIGHT),
        Math.max(MIN_MAP_HEIGHT, window.innerHeight * MAX_MAP_VIEWPORT_RATIO)
    )
}

function setMapHeight(height: number) {
    const frame = mapFrame.value
    if (!frame) return

    frame.style.flex = '0 0 auto'
    frame.style.height = `${clampMapHeight(height)}px`
}

function startMapResize(event: PointerEvent) {
    const frame = mapFrame.value
    if (!frame || event.button !== 0) return

    event.preventDefault()
    const handle = event.currentTarget as HTMLElement
    const startY = event.clientY
    const startHeight = frame.getBoundingClientRect().height

    handle.setPointerCapture(event.pointerId)
    document.body.classList.add('resizing-map')

    const move = (moveEvent: PointerEvent) => {
        setMapHeight(startHeight + moveEvent.clientY - startY)
    }
    const stop = () => {
        handle.removeEventListener('pointermove', move)
        handle.removeEventListener('pointerup', stop)
        handle.removeEventListener('pointercancel', stop)
        if (handle.hasPointerCapture(event.pointerId)) {
            handle.releasePointerCapture(event.pointerId)
        }
        document.body.classList.remove('resizing-map')
    }

    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', stop)
    handle.addEventListener('pointercancel', stop)
}

function resizeMapWithKeyboard(event: KeyboardEvent) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return

    const frame = mapFrame.value
    if (!frame) return

    event.preventDefault()
    const step = event.shiftKey ? 80 : 24
    setMapHeight(frame.getBoundingClientRect().height + (event.key === 'ArrowUp' ? -step : step))
}
</script>

<template>
    <div
        class="app-shell"
        :class="{ 'file-dragging': fileDragging }"
        v-if="loaded"
        @drop="dropHandler"
        @dragover="dragOverHandler"
        @dragenter="fileDragging = true"
        @dragleave="fileDragging = false"
    >
        <header class="site-header">
            <div class="header-inner">
                <a class="brand" href="/" aria-label="Minecraft Datapack Map">
                    <img src="/favicon.svg" alt="" />
                    <span>Datapack Map</span>
                </a>

                <nav class="header-tabs" :aria-label="i18n.t('ui.tabs.navigation')">
                    <button :class="{ active: activeHeader === 'map' }" type="button" @click="showPanel('structures')">
                        {{ i18n.t('ui.tabs.map') }}
                    </button>
                    <button :class="{ active: activeHeader === 'datapacks' }" type="button" @click="showPanel('datapacks')">
                        {{ i18n.t('ui.tabs.datapacks') }}
                    </button>
                    <button :class="{ active: activeHeader === 'about' }" type="button" @click="showPanel('about')">
                        {{ i18n.t('ui.tabs.about') }}
                    </button>
                </nav>

                <div class="header-actions">
                    <select :value="i18n.locale.value" :aria-label="i18n.t('locale.change_locale.title')" @change="changeLocale">
                        <option v-for="lang in i18n.availableLocales" :key="lang" :value="lang">
                            {{ i18n.t("locale.local_name", [], { locale: lang }) }}
                        </option>
                    </select>
                </div>
            </div>
        </header>

        <main class="workspace">
            <section class="toolbar">
                <Suspense>
                    <SettingsPanel compact />
                </Suspense>
                <MenuButtons detached />
            </section>

            <div ref="mapFrame" class="map-frame">
                <MainMap />
                <div
                    class="map-resizer"
                    role="separator"
                    aria-orientation="horizontal"
                    :aria-label="i18n.t('map.resize')"
                    :title="i18n.t('map.resize')"
                    tabindex="0"
                    @pointerdown="startMapResize"
                    @keydown="resizeMapWithKeyboard"
                >
                    <font-awesome-icon icon="fa-up-down" />
                </div>
            </div>

            <section class="control-panel">
                <div class="panel-tabs" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        :aria-selected="activePanel === 'structures'"
                        :class="{ active: activePanel === 'structures' }"
                        @click="activePanel = 'structures'"
                    >
                        <font-awesome-icon icon="fa-location-dot" />
                        <span>{{ i18n.t('ui.tabs.structures') }}</span>
                        <b v-if="searchStore.structures.size">{{ searchStore.structures.size }}</b>
                    </button>
                    <button
                        type="button"
                        role="tab"
                        :aria-selected="activePanel === 'biomes'"
                        :class="{ active: activePanel === 'biomes' }"
                        @click="activePanel = 'biomes'"
                    >
                        <font-awesome-icon icon="fa-earth-europe" />
                        <span>{{ i18n.t('ui.tabs.biomes') }}</span>
                        <b v-if="searchStore.biomes.size">{{ searchStore.biomes.size }}</b>
                    </button>
                    <button
                        type="button"
                        role="tab"
                        :aria-selected="activePanel === 'datapacks'"
                        :class="{ active: activePanel === 'datapacks' }"
                        @click="activePanel = 'datapacks'"
                    >
                        <font-awesome-icon icon="fa-file-zipper" />
                        <span>{{ i18n.t('ui.tabs.datapacks') }}</span>
                        <b v-if="datapackStore.datapacks.length > 1">{{ datapackStore.datapacks.length - 1 }}</b>
                    </button>
                    <button
                        type="button"
                        role="tab"
                        :aria-selected="activePanel === 'about'"
                        :class="{ active: activePanel === 'about' }"
                        @click="activePanel = 'about'"
                    >
                        <font-awesome-icon icon="fa-circle-question" />
                        <span>{{ i18n.t('ui.tabs.about') }}</span>
                    </button>
                </div>

                <div class="panel-content">
                    <Suspense v-if="activePanel === 'structures'">
                        <StructureDropdown embedded />
                    </Suspense>
                    <Suspense v-else-if="activePanel === 'biomes'">
                        <FindBiomeDropdown embedded />
                    </Suspense>
                    <div v-else-if="activePanel === 'datapacks'" class="datapacks-panel">
                        <DatapackList />
                    </div>
                    <div v-else class="about-panel">
                        <p>{{ i18n.t('ui.about.body') }}</p>
                        <Footer />
                    </div>
                </div>
            </section>
        </main>

        <div v-if="fileDragging" class="drop-overlay">
            <div>
                <font-awesome-icon icon="fa-file-zipper" />
                <span>{{ i18n.t('ui.drop_datapack') }}</span>
            </div>
        </div>
    </div>

    <div class="loading-screen" v-else>
        <div class="loading-mark"></div>
        <p>{{ i18n.t('ui.loading') }}</p>
    </div>
</template>

<style scoped>
.app-shell {
    width: 100%;
    height: 100%;
    background: #f2f4f6;
    color: #0f172a;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.site-header {
    height: 4.25rem;
    flex: 0 0 auto;
    background: #ffffff;
    border-bottom: 1px solid #e2e8f0;
    position: relative;
    z-index: 3000;
}

.header-inner {
    max-width: 1440px;
    height: 100%;
    margin: 0 auto;
    padding: 0 1.5rem;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 1rem;
    box-sizing: border-box;
}

.brand {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    color: #0f172a;
    text-decoration: none;
    font-weight: 800;
    font-size: 1.2rem;
    justify-self: start;
    white-space: nowrap;
}

.brand img {
    width: 2rem;
    height: 2rem;
    border-radius: 6px;
}

.header-tabs {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

.header-tabs button {
    border: 0;
    border-radius: 7px;
    background: transparent;
    color: #334155;
    padding: 0.55rem 1.1rem;
    font-weight: 700;
    cursor: pointer;
}

.header-tabs button:hover,
.header-tabs button.active {
    background: #f1f5f9;
    color: #2f6b43;
}

.header-actions {
    justify-self: end;
}

.header-actions select {
    height: 2.35rem;
    border: 1px solid #cbd5e1;
    border-radius: 7px;
    background: #ffffff;
    color: #334155;
    padding: 0 0.65rem;
    outline: none;
}

.workspace {
    width: min(100%, 1440px);
    min-height: 0;
    flex: 1 1 auto;
    margin: 0 auto;
    padding: 0.75rem 1.5rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    box-sizing: border-box;
}

.toolbar {
    flex: 0 0 auto;
    min-height: 3.1rem;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.75rem;
    padding: 0.35rem;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 9px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.toolbar > :last-child {
    padding-right: 0.25rem;
    width: max-content;
}

.map-frame {
    flex: 1 1 auto;
    min-height: 22rem;
    border: 1px solid #d6dde5;
    border-radius: 9px;
    overflow: hidden;
    position: relative;
    background: #ffffff;
    box-shadow: 0 3px 12px rgba(15, 23, 42, 0.08);
}

.map-resizer {
    position: absolute;
    z-index: 1200;
    left: 50%;
    bottom: 0.4rem;
    width: 2.6rem;
    height: 1.15rem;
    transform: translateX(-50%);
    border: 1px solid #cbd5e1;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.94);
    color: #475569;
    display: grid;
    place-items: center;
    cursor: ns-resize;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.14);
    touch-action: none;
}

.map-resizer:hover,
.map-resizer:focus-visible {
    border-color: #8fc39e;
    background: #e8f4ec;
    color: #2f6b43;
    outline: none;
}

.map-resizer svg {
    font-size: 0.72rem;
    pointer-events: none;
}

:global(body.resizing-map) {
    cursor: ns-resize;
    user-select: none;
}

.control-panel {
    flex: 0 0 auto;
    background: #ffffff;
    border: 1px solid #d6dde5;
    border-radius: 9px;
    overflow: hidden;
    box-shadow: 0 3px 12px rgba(15, 23, 42, 0.08);
}

.panel-tabs {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.panel-tabs button {
    min-width: 0;
    height: 3.45rem;
    border: 0;
    border-right: 1px solid #e2e8f0;
    border-bottom: 3px solid transparent;
    background: transparent;
    color: #475569;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-weight: 700;
    cursor: pointer;
}

.panel-tabs button:last-child {
    border-right: 0;
}

.panel-tabs button:hover {
    background: #f1f5f9;
}

.panel-tabs button.active {
    color: #2f6b43;
    background: #ffffff;
    border-bottom-color: #4d8c61;
}

.panel-tabs b {
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.3rem;
    border-radius: 999px;
    background: #dff0e4;
    color: #2f6b43;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    box-sizing: border-box;
}

.panel-content {
    min-height: 11rem;
    max-height: min(31rem, 39vh);
    overflow: auto;
}

.datapacks-panel {
    padding: 1rem;
    display: flex;
    justify-content: center;
}

.datapacks-panel :deep(.datapack_list) {
    width: min(100%, 58rem);
}

.about-panel {
    min-height: 10rem;
    padding: 1rem 1.25rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
}

.about-panel > p {
    max-width: 46rem;
    margin: 0;
    color: #475569;
    line-height: 1.6;
}

.drop-overlay {
    position: fixed;
    inset: 0;
    z-index: 10000;
    background: rgba(15, 23, 42, 0.45);
    display: grid;
    place-items: center;
    pointer-events: none;
}

.drop-overlay > div {
    min-width: min(28rem, calc(100vw - 2rem));
    padding: 2rem;
    border: 2px dashed #8fc39e;
    border-radius: 10px;
    background: #ffffff;
    color: #2f6b43;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.8rem;
    font-size: 1.1rem;
    font-weight: 700;
    box-sizing: border-box;
}

.drop-overlay svg {
    width: 2rem;
    height: 2rem;
}

.loading-screen {
    width: 100%;
    height: 100%;
    background: #f2f4f6;
    color: #475569;
    display: grid;
    place-content: center;
    justify-items: center;
    gap: 1rem;
    font-weight: 700;
}

.loading-screen p {
    margin: 0;
}

.loading-mark {
    width: 2.3rem;
    height: 2.3rem;
    border: 3px solid #cbd5e1;
    border-top-color: #4d8c61;
    border-radius: 999px;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 900px) {
    .header-inner {
        grid-template-columns: auto 1fr auto;
    }

    .header-tabs {
        justify-self: center;
    }

    .header-tabs button {
        padding: 0.55rem 0.7rem;
    }

    .brand span {
        display: none;
    }

    .toolbar {
        grid-template-columns: 1fr;
    }

    .toolbar > :last-child {
        padding: 0;
        width: auto;
    }

    .map-frame {
        min-height: 20rem;
    }
}

@media (max-height: 800px) and (min-width: 621px) {
    .site-header {
        height: 3.5rem;
    }

    .workspace {
        padding-top: 0.5rem;
        padding-bottom: 0.75rem;
        gap: 0.5rem;
    }

    .toolbar {
        min-height: 2.8rem;
        padding: 0.25rem;
    }

    .map-frame {
        min-height: 13rem;
    }

    .panel-tabs button {
        height: 2.8rem;
    }

    .panel-content {
        max-height: min(24rem, 30vh);
    }
}

@media (max-width: 620px) {
    .site-header {
        height: 3.8rem;
    }

    .header-inner,
    .workspace {
        padding-left: 0.5rem;
        padding-right: 0.5rem;
    }

    .workspace {
        overflow-y: auto;
        overscroll-behavior: contain;
    }

    .header-tabs button {
        padding: 0.45rem;
        font-size: 0.78rem;
    }

    .header-actions select {
        max-width: 5.4rem;
    }

    .panel-tabs button {
        height: 3.2rem;
        font-size: 0.76rem;
        gap: 0.25rem;
    }

    .panel-tabs b {
        display: none;
    }

    .map-frame {
        min-height: 16rem;
    }

    .about-panel {
        flex-direction: column;
    }
}
</style>
